import { useMemo } from 'react';
import { Button } from '@savvy-kit/components/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@savvy-kit/components/dropdown-menu';
import { Text } from '@savvy-kit/foundations/text';
import { Funnel } from 'lucide-react';

import type { CenterOfInfluenceRepSpecialty } from '@savvy/constants/generated/gql-enums';

import type { ContactWebFilters } from './filter-graph';
import type { ContactWebGraph } from './types';
import { SPECIALTY_LABELS, SPECIALTY_OPTIONS } from '../constants';

type Props = {
  readonly graph: ContactWebGraph;
  readonly filters: ContactWebFilters;
  readonly onChange: (filters: ContactWebFilters) => void;
};

export function ContactWebFilter({ graph, filters, onChange }: Props) {
  const advisorOptions = useMemo(() => {
    const names = new Set<string>();
    for (const node of graph.nodes) {
      if (node.kind === 'household' && node.advisorName !== null) names.add(node.advisorName);
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [graph.nodes]);

  const specialtyOptions = useMemo(() => {
    const specialties = new Set<CenterOfInfluenceRepSpecialty>();
    for (const node of graph.nodes) {
      if (node.kind === 'professional') {
        for (const specialty of node.specialties) specialties.add(specialty);
      }
    }
    return SPECIALTY_OPTIONS.filter((specialty) => specialties.has(specialty));
  }, [graph.nodes]);

  const activeCount = filters.advisorNames.size + filters.specialties.size;

  function toggleAdvisor(name: string, checked: boolean) {
    const next = new Set(filters.advisorNames);
    if (checked) next.add(name);
    else next.delete(name);
    onChange({ ...filters, advisorNames: next });
  }

  function toggleSpecialty(specialty: CenterOfInfluenceRepSpecialty, checked: boolean) {
    const next = new Set(filters.specialties);
    if (checked) next.add(specialty);
    else next.delete(specialty);
    onChange({ ...filters, specialties: next });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button variant="secondary" icon={<Funnel className="size-4" />}>
            Filter{activeCount > 0 ? ` (${activeCount})` : ''}
          </Button>
        }
      />
      <DropdownMenuContent align="start" className="w-48">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            Advisor{filters.advisorNames.size > 0 ? ` (${filters.advisorNames.size})` : ''}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="max-h-96 w-56 overflow-auto">
            {advisorOptions.length === 0 ? (
              <Text variant="body-sm" color="secondary" className="px-2 py-1.5">
                No advisors
              </Text>
            ) : (
              advisorOptions.map((name) => (
                <DropdownMenuCheckboxItem
                  key={name}
                  checked={filters.advisorNames.has(name)}
                  onCheckedChange={(checked) => toggleAdvisor(name, checked)}
                >
                  <span className="min-w-0 truncate">{name}</span>
                </DropdownMenuCheckboxItem>
              ))
            )}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            Specialties{filters.specialties.size > 0 ? ` (${filters.specialties.size})` : ''}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="max-h-96 w-56 overflow-auto">
            {specialtyOptions.length === 0 ? (
              <Text variant="body-sm" color="secondary" className="px-2 py-1.5">
                No specialties
              </Text>
            ) : (
              specialtyOptions.map((specialty) => (
                <DropdownMenuCheckboxItem
                  key={specialty}
                  checked={filters.specialties.has(specialty)}
                  onCheckedChange={(checked) => toggleSpecialty(specialty, checked)}
                >
                  <span className="min-w-0 truncate">{SPECIALTY_LABELS[specialty]}</span>
                </DropdownMenuCheckboxItem>
              ))
            )}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
